"use client";

import { c as _c } from "react/compiler-runtime";
import { StepList } from "../step-list";
import type { Orientation } from "../types";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CARE_PLAN_PRINT_GRAPH_CSS, fitCarePlanGraph, measureCarePlanGraph } from "@/lib/print-care-plan-canvas";
import { CarePlanPrintSnapshot, useCarePlanPrintReadiness } from "./care-plan-print-snapshot";

/** Hide content only after measuring. Visibility retains all real graph geometry. */
export function makeCarePlanStructuralPreview(canvas: HTMLElement) {
  const clone = canvas.cloneNode(true) as HTMLElement;
  clone.querySelectorAll<HTMLElement>("[data-care-plan-card-content], [data-care-plan-branch-label], [data-care-plan-connector-label]").forEach(node => {
    node.style.setProperty("visibility", "hidden", "important");
  });
  // Linked messages live inside their parent's content, but their outlines remain.
  clone.querySelectorAll<HTMLElement>("[data-step-card], [data-step-card] > div").forEach(node => {
    node.style.setProperty("visibility", "visible", "important");
  });
  clone.querySelectorAll("[id]").forEach(node => node.removeAttribute("id"));
  clone.querySelectorAll("script, iframe, object, embed").forEach(node => node.remove());
  return clone;
}

/** Trace measured outlines/connections, not a second graph layout. The overlay
 * lives outside the graph transform so tiny fitted graphs retain a .75px stroke. */
export function makeCarePlanPreviewStrokes(canvas: HTMLElement, bounds: ReturnType<typeof measureCarePlanGraph>, scale: number, previewSize: number) {
  const namespace = "http://www.w3.org/2000/svg";
  const overlay = document.createElementNS(namespace, "svg");
  overlay.setAttribute("width", String(previewSize));
  overlay.setAttribute("height", String(previewSize));
  overlay.setAttribute("viewBox", `0 0 ${previewSize} ${previewSize}`);
  overlay.setAttribute("aria-hidden", "true");
  overlay.setAttribute("data-care-plan-preview-strokes", "");
  Object.assign(overlay.style, {
    position: "absolute",
    left: "0",
    top: "0",
    pointerEvents: "none"
  });
  const origin = canvas.getBoundingClientRect();
  const strokeWidth = 0.75;
  const append = (name: "rect" | "line", attributes: Record<string, number>) => {
    const shape = document.createElementNS(namespace, name);
    for (const [key, value] of Object.entries(attributes)) shape.setAttribute(key, String(value));
    shape.setAttribute("fill", "none");
    shape.setAttribute("stroke", "#555");
    shape.setAttribute("stroke-width", String(strokeWidth));
    overlay.appendChild(shape);
  };
  canvas.querySelectorAll<HTMLElement>("[data-step-card] > div, .bg-border, .border-t, .border-b, .border-l, .border-r").forEach(node => {
    const card = node.matches("[data-step-card] > div");
    // Card descendants contain decorative tag/sequences borders, not graph edges.
    if (!card && node.closest("[data-step-card], [data-care-plan-branch-label]")) return;
    const rect = node.getBoundingClientRect();
    const x = (rect.left - origin.left - bounds.left) * scale;
    const y = (rect.top - origin.top - bounds.top) * scale;
    const width = rect.width * scale;
    const height = rect.height * scale;
    if (card) {
      const radius = parseFloat(getComputedStyle(node).borderTopLeftRadius) || 0;
      append("rect", {
        x,
        y,
        width,
        height,
        rx: radius * scale
      });
    } else if (node.classList.contains("bg-border")) {
      append("line", width >= height ? {
        x1: x,
        y1: y + height / 2,
        x2: x + width,
        y2: y + height / 2
      } : {
        x1: x + width / 2,
        y1: y,
        x2: x + width / 2,
        y2: y + height
      });
    } else {
      const style = getComputedStyle(node);
      if (parseFloat(style.borderTopWidth)) append("line", {
        x1: x,
        y1: y,
        x2: x + width,
        y2: y
      });
      if (parseFloat(style.borderBottomWidth)) append("line", {
        x1: x,
        y1: y + height,
        x2: x + width,
        y2: y + height
      });
      if (parseFloat(style.borderLeftWidth)) append("line", {
        x1: x,
        y1: y,
        x2: x,
        y2: y + height
      });
      if (parseFloat(style.borderRightWidth)) append("line", {
        x1: x + width,
        y1: y,
        x2: x + width,
        y2: y + height
      });
    }
  });
  return overlay;
}
export function CarePlanPrintPreview(t0) {
  const $ = _c(26);
  const {
    direction,
    onReady
  } = t0;
  const canvasRef = useRef(null);
  const previewRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [snapshotHost, setSnapshotHost] = useState(null);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setSnapshotHost(document.body);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const {
    loading,
    error
  } = useCarePlanPrintReadiness();
  let t3;
  if ($[2] !== direction || $[3] !== error || $[4] !== loading || $[5] !== onReady) {
    t3 = () => {
      setReady(false);
      onReady(direction, null, error);
      if (loading || error) {
        return;
      }
      let cancelled = false;
      let frame = 0;
      let fontsReady = false;
      const canvas = canvasRef.current;
      const preview = previewRef.current;
      if (!canvas || !preview) {
        return;
      }
      const update = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          if (cancelled || !fontsReady) {
            return;
          }
          ;
          try {
            const previewSize = preview.offsetWidth;
            if (previewSize <= 0) {
              return;
            }
            const bounds = measureCarePlanGraph(canvas);
            const scale = fitCarePlanGraph(bounds, {
              width: previewSize,
              height: previewSize
            });
            const clone = makeCarePlanStructuralPreview(canvas);
            Object.assign(clone.style, {
              position: "absolute",
              left: `${-bounds.left}px`,
              top: `${-bounds.top}px`,
              width: `${bounds.layoutWidth}px`,
              height: `${bounds.layoutHeight}px`
            });
            const fit = document.createElement("div");
            Object.assign(fit.style, {
              position: "absolute",
              left: "0",
              top: "0",
              width: "0",
              height: "0",
              transformOrigin: "0 0",
              transform: `scale(${scale})`
            });
            fit.appendChild(clone);
            preview.replaceChildren(fit, makeCarePlanPreviewStrokes(canvas, bounds, scale, previewSize));
            setReady(true);
            onReady(direction, canvas);
          } catch (t4) {
            const reason = t4;
            onReady(direction, null, reason);
          }
        });
      };
      const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
      observer?.observe(canvas);
      observer?.observe(preview);
      const mutations = new MutationObserver(update);
      mutations.observe(canvas, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });
      Promise.resolve(document.fonts?.ready).then(() => {
        if (!cancelled) {
          fontsReady = true;
          update();
        }
      }, reason_0 => {
        if (!cancelled) {
          onReady(direction, null, reason_0);
        }
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(frame);
        observer?.disconnect();
        mutations.disconnect();
        preview.replaceChildren();
        onReady(direction, null);
      };
    };
    $[2] = direction;
    $[3] = error;
    $[4] = loading;
    $[5] = onReady;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== direction || $[8] !== error || $[9] !== loading || $[10] !== onReady || $[11] !== snapshotHost) {
    t4 = [direction, loading, error, onReady, snapshotHost];
    $[7] = direction;
    $[8] = error;
    $[9] = loading;
    $[10] = onReady;
    $[11] = snapshotHost;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== direction || $[14] !== snapshotHost) {
    t5 = snapshotHost && createPortal(<div aria-hidden="true" inert={true} style={{
      position: "fixed",
      left: -100000,
      top: 0,
      width: "max-content",
      pointerEvents: "none"
    }}><CarePlanPrintSnapshot orientation={direction}><div data-care-plan-print-snapshot={direction} className="care-print-graph" ref={canvasRef} style={{
          width: "max-content",
          padding: 8
        }}><StepList /></div></CarePlanPrintSnapshot></div>, snapshotHost);
    $[13] = direction;
    $[14] = snapshotHost;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      width: "100%",
      aspectRatio: "1",
      background: "#fff"
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div ref={previewRef} style={{
      position: "absolute",
      inset: 0
    }} />;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== direction) {
    t8 = <div aria-hidden="true" inert={true} data-care-plan-print-preview={direction} className="pointer-events-none relative mt-3 overflow-hidden rounded-sm border border-border bg-background-secondary" style={t6}>{t7}</div>;
    $[18] = direction;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== ready) {
    t9 = !ready && <span className="text-caption text-foreground-secondary">Preparing preview…</span>;
    $[20] = ready;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t5 || $[23] !== t8 || $[24] !== t9) {
    t10 = <>{t5}{t8}{t9}</>;
    $[22] = t5;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}

/** Scoped graph styles only: never install popup html/body rules in the app. */
export function CarePlanPrintGraphStyles() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <style>{CARE_PLAN_PRINT_GRAPH_CSS}</style>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}